import React, {
  createContext,
  useState,
  useEffect,
  useContext,
  useCallback,
} from 'react'
import apiService from '../services/ApiService'
import { shouldDefaultCountryToEmpty } from '../utils/domainUtils'
const CountryLawContext = createContext()
const countryDomainMap = {
  'uaetaxlaws.com': 'UAE',
  'www.uaetaxlaws.com': 'UAE',
  'ksataxlaws.com': 'KSA',
  'www.ksataxlaws.com': 'KSA',
  'kuwaittaxlaws.com': 'Kuwait',
  'www.kuwaittaxlaws.com': 'Kuwait',
  'qatartaxlaws.com': 'Qatar',
  'www.qatartaxlaws.com': 'Qatar',
  'omantaxlaws.com': 'Oman',
  'www.omantaxlaws.com': 'Oman',
  'bahraintaxlaws.com': 'Bahrain',
  'www.bahraintaxlaws.com': 'Bahrain',
  'gcctaxlaws.com': 'GCC',
  'www.gcctaxlaws.com': 'GCC',
  // Add localhost and IP mapping for development
  localhost: 'GCC',
  '127.0.0.1': 'GCC',
}

const generateSiteName = (countryName, domain) => {
  // For localhost and development, always return GCC

  if (isLocalEnvironment(domain)) {
    return 'GCC TAX LAWS'
  }

  // For specific country domains, use country-specific names
  const siteNameMap = {
    UAE: 'UAE TAX LAWS',
    KSA: 'KSA TAX LAWS',
    Kuwait: 'KUWAIT TAX LAWS',
    Qatar: 'QATAR TAX LAWS',
    Oman: 'OMAN TAX LAWS',
    Bahrain: 'BAHRAIN TAX LAWS',
    GCC: 'GCC TAX LAWS',
  }
  return siteNameMap[countryName] || 'GCC TAX LAWS'
}
const isLocalEnvironment = (domain) => {
  // we have 2 different DB(dev, prod) with different data sets
  // uaetaxlaws.com, ksataxlaws.com, kuwaittaxlaws.com, qatartaxlaws.com, omantaxlaws.com, bahraintaxlaws.com, localhost - REACT_APP_ENVIRONMENT=dev
  // but for seo purpose we dont want to show ksa data on uaetaxlaws.com, similar for other country specific domains
  // gcctaxlaws.com - REACT_APP_ENVIRONMENT=prod - we want to show all 6 countries data if they are passed from review team
  // so for local environment testing we will show all 6 countries only for localhost and private IP address access

  // Check for localhost variations
  if (
    domain === 'localhost' ||
    domain === '127.0.0.1'
    // || process.env.REACT_APP_ENVIRONMENT === 'dev'
  ) return true

  // Check for private IP ranges only
  if (domain.match(/^192\.168\.\d+\.\d+$/)) return true // 192.168.0.0/16
  if (domain.match(/^10\.\d+\.\d+\.\d+$/)) return true // 10.0.0.0/8
  if (domain.match(/^172\.(1[6-9]|2\d|3[01])\.\d+\.\d+$/)) return true // 172.16.0.0/12
  // Any IP address - domain.match(/^\d+\.\d+\.\d+\.\d+$/)
  return false
}
export const CountryLawProvider = ({ children }) => {
  const [countriesWithLaws, setCountriesWithLaws] = useState([])

  // uae(are), bahrain, kuwait, oman, qatar, ksa, gcc, etc.
  const [selectedCountry, setSelectedCountry] = useState(() => {
    const domain = window.location.hostname
    if (shouldDefaultCountryToEmpty(domain)) {
      return ''
    }
    const savedCountry = localStorage.getItem('selectedCountry')
    return savedCountry || '' // 'UAE' <-- countryName attribute used of the country object in all the sidebar filters, and same value we are sending to backend to fetch data
  })
  const [availableLaws, setAvailableLaws] = useState([]) // all laws of the selected country
  const [availableTaxTransparencies, setAvailableTaxTransparencies] = useState(
    [],
  ) // all tax transparencies of the selected country

  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  // this currentDomain state variable will only be useful to set laws for special domains like testgcctaxlaws.com and localhost, for other domains, the backend will automatically filter the data based on the domain of the request
  const [currentDomain, setCurrentDomain] = useState('') // uaetaxlaws.com, gcctaxlaws.com, etc.

  const [siteName, setSiteName] = useState('GCC TAX LAWS') // UAE Tax Laws, GCC Tax Laws, etc.
  const [countryFlag, setCountryFlag] = useState('GCC') // ae, bh, kw, om, qa, sa

  const fetchCountriesWithLaws = useCallback(async () => {
    setLoading(true)
    try {
      // The server will automatically filter based on the domain of the request, only one country's data will be returned, testgcctaxlaws.com and localhost domains will return UAE data and for gcctaxlaws.com, all countries data will be returned

      // Get the current domain
      const domain = window.location.hostname
      setCurrentDomain(domain)
      const shouldKeepCountryUnselected = shouldDefaultCountryToEmpty(domain)

      // Get expected country name from domain mapping

      const expectedCountryName =
        countryDomainMap[domain] ||
        countryDomainMap[`www.${domain}`] ||
        (isLocalEnvironment(domain) ? 'GCC' : null)

      const countries = await apiService.getCountriesWithLaws()

      if (countries && Array.isArray(countries) && countries.length > 0) {
        setCountriesWithLaws(countries)

        let targetCountry = null

        // If we have an expected country from domain, try to find it

        if (expectedCountryName && expectedCountryName !== 'GCC') {
          targetCountry = countries.find(
            (c) =>
              c.countryName.toLowerCase() === expectedCountryName.toLowerCase()
          )
        }

        // If no target country found (or it's a GCC/localhostingTesting domain), use the first available country

        if (
          !targetCountry &&
          countries.length > 0 &&
          !shouldKeepCountryUnselected
        ) {
          targetCountry = countries[0]
        }

        if (targetCountry) {
          // Set site name based on domain and target country
          let siteNameToSet = 'GCC TAX LAWS' // Default fallback
          if (expectedCountryName && expectedCountryName !== 'GCC') {
            // For country-specific domains, use the expected country name
            siteNameToSet = generateSiteName(expectedCountryName, domain)
          } else if (isLocalEnvironment(domain)) {
            // For localhost/development, use GCC
            siteNameToSet = 'GCC TAX LAWS'
          } else {
            // For gcctaxlaws.com or unknown domains, use GCC
            siteNameToSet = 'GCC TAX LAWS'
          }
          setSiteName(siteNameToSet)
          // Set country flag (always GCC for now)
          setCountryFlag('GCC')
          // Set the selected country and available laws
          setSelectedCountry(targetCountry.countryName)
          setAvailableLaws(targetCountry.laws || [])
          setAvailableTaxTransparencies(targetCountry.taxTransparencies || [])
        } else {
          // Fallback if no countries available
          setSiteName('GCC TAX LAWS')
          setCountryFlag('GCC')
          setSelectedCountry('')
          setAvailableLaws([])
          setAvailableTaxTransparencies([])
        }
      } else {
        // No countries received - set based on domain anyway
        const expectedCountryName =
          countryDomainMap[domain] || countryDomainMap[`www.${domain}`]
        if (expectedCountryName && expectedCountryName !== 'GCC') {
          setSiteName(generateSiteName(expectedCountryName, domain))
        } else {
          setSiteName('GCC TAX LAWS')
        }
        setCountryFlag('GCC')
        setSelectedCountry('')
        setAvailableLaws([])
        setAvailableTaxTransparencies([])
      }
      setError(null)
    } catch (err) {
      // Even on error, set site name based on domain
      const domain = window.location.hostname
      const expectedCountryName =
        countryDomainMap[domain] || countryDomainMap[`www.${domain}`]
      if (expectedCountryName && expectedCountryName !== 'GCC') {
        setSiteName(generateSiteName(expectedCountryName, domain))
      } else {
        setSiteName('GCC TAX LAWS')
      }
      setError('Failed to load tax laws. Please try again later.')
      console.error('Error fetching countries with laws:', err)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchCountriesWithLaws()
  }, [fetchCountriesWithLaws])

  useEffect(() => {
    if (countriesWithLaws.length > 0 && selectedCountry) {
      const matchingCountry = countriesWithLaws.find(
        (c) => c.countryName.toLowerCase() === selectedCountry.toLowerCase()
      )

      if (matchingCountry && matchingCountry.laws) {
        setAvailableLaws(matchingCountry?.laws || [])
      } else {
        setAvailableLaws([])
      }

      if (matchingCountry && matchingCountry.taxTransparencies) {
        setAvailableTaxTransparencies(matchingCountry?.taxTransparencies || [])
      } else {
        setAvailableTaxTransparencies([])
      }
    }
  }, [selectedCountry, countriesWithLaws])

  useEffect(() => {
    localStorage.setItem('selectedCountry', selectedCountry)
  }, [selectedCountry])

  return (
    <CountryLawContext.Provider
      value={{
        countriesWithLaws,
        refreshCountriesData: fetchCountriesWithLaws,
        selectedCountry,
        setSelectedCountry,
        availableLaws, // all laws of all countries, possible only for gcctaxlaws.com domain, for other domains, only one country's data will be returned
        setAvailableLaws,
        availableTaxTransparencies, // all tax transparencies of all countries, possible only for gcctaxlaws.com domain, for other domains, only one country's data will be returned
        setAvailableTaxTransparencies,
        currentDomain,
        siteName,
        countryFlag,
        loading,
        error,
      }}
    >
      {children}
    </CountryLawContext.Provider>
  )
}

export const useCountryLaw = () => {
  const context = useContext(CountryLawContext)
  if (context === undefined) {
    throw new Error('useCountry must be used within a CountryProvider')
  }
  return context
}
